import { Card, Group, Stack, Text } from "@mantine/core";
import { LogoImg } from "./LogoImg";
import { PriceBadge } from "./PriceBadge";
import type { ChangeType } from "../utils/price";

interface FeedCardProps {
  changeId: string;
  productName: string;
  productLineName: string | null;
  brandName: string;
  brandDomain: string | null;
  retailerName: string;
  retailerDomain: string | null;
  changeType: string;
  previousPrice: string | null;
  newPrice: string;
  priceDelta: string | null;
  priceDeltaPct: string | null;
  detectedAt: string;
  imageUrl: string | null;
  productUrl: string | null;
  onViewHistory?: () => void;
}

const formatRelativeTime = (isoDate: string): string => {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatAbsoluteTime = (isoDate: string): string =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(isoDate));

export function FeedCard(props: FeedCardProps) {
  const {
    productName,
    brandName,
    brandDomain,
    retailerName,
    retailerDomain,
    changeType,
    previousPrice,
    newPrice,
    priceDeltaPct,
    detectedAt,
    onViewHistory,
  } = props;

  return (
    <Card
      padding="md"
      withBorder
      style={{ cursor: onViewHistory ? "pointer" : undefined }}
      onClick={onViewHistory}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group wrap="nowrap" gap="sm" style={{ flex: 1, minWidth: 0 }}>
          {brandDomain && <LogoImg domain={brandDomain} size={36} alt={brandName} />}
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} lineClamp={1}>
              {productName}
            </Text>
            <Group gap="xs">
              {retailerDomain && (
                <LogoImg domain={retailerDomain} size={16} alt={retailerName} />
              )}
              <Text size="xs" c="dimmed">
                {retailerName}
              </Text>
            </Group>
          </Stack>
        </Group>

        <PriceBadge
          currentPrice={parseFloat(newPrice)}
          previousPrice={previousPrice !== null ? parseFloat(previousPrice) : null}
          changeType={changeType as ChangeType}
          percentChange={priceDeltaPct !== null ? parseFloat(priceDeltaPct) : null}
          size="md"
        />
      </Group>

      <Group justify="flex-end" mt="xs">
        <Text size="xs" c="dimmed" title={formatAbsoluteTime(detectedAt)}>
          {formatRelativeTime(detectedAt)}
        </Text>
      </Group>
    </Card>
  );
}
